import { HotmartRum } from "@commons/observability"
import { ClubSlugHandler } from "./clubSlug.handler"
import { RouterHandlerInterface } from "./types/routeHandler.interface"
import { ClubResponseInterface } from "./types/clubResponse.interface"
import { CustomTheme } from "../../types/customTheme.interface"
import { ModeEnum } from "../../types/mode.enum"
import { ApplicationConsumerInformation } from "../../util/brand/types/ApplicationConsumerInformation.interface"
import { TrackingEvent } from "../../trackingEvent"
import { EventInterface } from "../../types/event.interface"
import { Realm } from "../../../realms/types/realm.interface"
import { HttpRequest } from "../../util/httpRequest"

const url = {
    production: "https://api-content-platform-space-gateway.cp.hotmart.com",
    staging: "https://api-content-platform-space-gateway.buildstaging.com",
    local: "https://api-content-platform-space-gateway.buildstaging.com"
}

export class ClubTheme implements CustomTheme {
    defaultMode = ModeEnum.DARK
    applicationConsumerInformation: ApplicationConsumerInformation

    private baseUrl = url[global.ENV]
    private slugHandler: RouterHandlerInterface
    private route: string | undefined
    private trackingEvent: TrackingEvent
    private httpRequest: HttpRequest

    constructor(
        applicationConsumerInformation: ApplicationConsumerInformation,
        trackingEvent: TrackingEvent
    ) {
        this.slugHandler = new ClubSlugHandler()
        this.applicationConsumerInformation = {
            ...applicationConsumerInformation,
            realm: applicationConsumerInformation.realm ?? "club"
        }
        this.route = this.slugHandler.getRoute()
        this.trackingEvent = trackingEvent
        this.httpRequest = new HttpRequest(this.baseUrl)
    }

    get brand(): string | undefined {
        return this.slugHandler.getBrandSlug()
    }

    private get apiFullUrl(): string {
        return `${this.baseUrl}/${this.route}`
    }

    private getErrorEvent(error: any): EventInterface {
        return {
            brand: this.brand,
            error: {
                code: String(error?.code),
                message: error?.message || "No error message",
                status: error?.status || 500,
                path: this.apiFullUrl
            }
        }
    }

    private normalize(data: ClubResponseInterface): Realm {
        const settings = data.settings
        const backgroundImage = data?.settings?.backgroundImage

        const { imageUrl } =
            settings.logos?.find(
                ({ type }) =>
                    type.toLowerCase() === settings.theme?.toLowerCase()
            ) || {}

        return {
            brandName: data.name,
            mode: data.settings.theme?.toLowerCase(),
            backgroundUrl: {
                desktop: backgroundImage?.imageUrlBackground,
                tablet: backgroundImage?.imageUrlBackground2,
                mobile: backgroundImage?.imageUrlBackground1
            },
            ...(imageUrl && {
                logoHeaderUrl: { light: imageUrl, dark: imageUrl }
            })
        } as Realm
    }

    async getTheme(): Promise<Realm> {
        if (!this.brand) return {} as Realm

        try {
            const data = await this.httpRequest.get<ClubResponseInterface>(
                `/${this.route}`
            )

            return this.normalize(data)
        } catch (error: any) {
            const event = this.getErrorEvent(error)

            this.trackingEvent.send(event)

            if (error.requestInfo) {
                HotmartRum.captureException(error)

                return {} as Realm
            }

            HotmartRum.captureException(
                new Error(
                    `[API Club error] - ${error?.code}: ${error?.message}`
                )
            )

            return {} as Realm
        }
    }
}
